import { useAccount, useDisconnect, useSwitchChain, useBalance } from 'wagmi';
import { useState, useEffect } from 'react';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { supportedChains } from '../config/chains';

export function WalletConnect() {
  const { address, isConnected, chainId } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const [showChainWarning, setShowChainWarning] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  
  // Check if current chain is supported
  const isSupportedChain = chainId ? supportedChains.some(chain => chain.id === chainId) : false;
  const currentChain = chainId ? supportedChains.find(chain => chain.id === chainId) : null;
  
  const { data: balanceData } = useBalance({ 
    address, 
    chainId: currentChain?.id || 1,
    query: {
      enabled: isConnected && !!address && !!currentChain,
    }
  });
  const balance = balanceData ? Number(balanceData.formatted).toFixed(4) : '0';
  const nativeSymbol = currentChain?.nativeCurrency?.symbol || 'ETH';

  useEffect(() => {
    if (isConnected && chainId) {
      // Only show warning if chain is not supported
      setShowChainWarning(!isSupportedChain);
    } else {
      setShowChainWarning(false);
    }
  }, [isConnected, chainId, isSupportedChain]);

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const handleSwitchChain = () => {
    if (switchChain && supportedChains.length > 0) {
      // Switch to first supported chain (usually Ethereum)
      switchChain({ chainId: supportedChains[0].id });
    }
  };

  if (isConnected && address) {
    const isCorrectChain = isSupportedChain;
    
    return (
      <div className="relative flex flex-col items-end gap-2 flex-shrink-0">
        {showChainWarning && !isCorrectChain && (
          <div className="px-4 py-2.5 rounded-lg border animate-slide-up" style={{ backgroundColor: '#1A1D29', borderColor: '#2D2F36', borderRadius: 'var(--radius-sm)' }}>
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4" style={{ color: '#E5E5E5' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-xs" style={{ fontWeight: 500, color: '#E5E5E5' }}>Unsupported Network</span>
              <button
                onClick={handleSwitchChain}
                disabled={isSwitching}
                className="px-3 py-1 bg-[#2081E2] text-white hover:bg-[#1a6bc4] rounded-lg font-semibold text-xs transition-colors disabled:opacity-50"
                style={{ borderRadius: 'var(--radius-sm)', fontWeight: 600 }}
              >
                {isSwitching ? 'Switching...' : `Switch to ${supportedChains[0]?.name || 'Supported'}`}
              </button>
            </div>
          </div>
        )}
        
        <div className="relative">
          <button
            onClick={() => setShowWalletDetails(!showWalletDetails)}
            className="px-2 sm:px-4 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-3 rounded-lg transition-all border"
            style={{ 
              backgroundColor: '#1A1D29', 
              borderColor: isCorrectChain ? '#2081E2' : '#2D2F36', 
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0D1117', borderColor: isCorrectChain ? '#2081E2' : '#2D2F36', borderRadius: 'var(--radius-sm)' }}>
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: isCorrectChain ? '#2081E2' : 'rgba(229, 229, 229, 0.7)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            
            <div className="flex flex-col items-start min-w-0">
              {isCorrectChain && balanceData ? (
                <>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs sm:text-sm font-semibold truncate" style={{ fontWeight: 600, color: '#E5E5E5' }}>
                      {balance} {nativeSymbol}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[10px] sm:text-xs font-mono truncate" style={{ fontWeight: 500, color: 'rgba(229, 229, 229, 0.7)' }}>{formatAddress(address)}</span>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-[10px] sm:text-xs" style={{ fontWeight: 500, color: 'rgba(229, 229, 229, 0.7)' }}>Connected</span>
                  <span className="text-xs sm:text-sm font-semibold font-mono truncate" style={{ fontWeight: 600, color: '#E5E5E5' }}>{formatAddress(address)}</span>
                </>
              )}
            </div>
          </button>

          {showWalletDetails && (
            <div 
              className="absolute top-full right-0 mt-2 w-[340px] sm:w-80 border rounded-lg shadow-lg z-50"
              style={{ 
                backgroundColor: '#1A1D29', 
                borderColor: '#2D2F36', 
                borderRadius: 'var(--radius-lg)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
            >
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b" style={{ borderColor: '#2D2F36' }}>
                <h3 className="text-sm font-semibold" style={{ fontWeight: 600, color: '#E5E5E5' }}>Wallet Details</h3>
                <button
                  onClick={() => setShowWalletDetails(false)}
                  className="p-1 rounded transition-colors"
                  style={{ color: 'rgba(229, 229, 229, 0.7)', borderRadius: 'var(--radius-sm)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide" style={{ fontWeight: 500, color: 'rgba(229, 229, 229, 0.7)' }}>Address</div>
                <a
                  href={currentChain?.blockExplorers?.default?.url ? `${currentChain.blockExplorers.default.url}/address/${address}` : `https://etherscan.io/address/${address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 px-3 py-2 rounded border transition-colors break-all"
                  style={{ 
                    backgroundColor: '#0D1117', 
                    borderColor: '#2D2F36', 
                    borderRadius: 'var(--radius-sm)',
                    color: '#2081E2',
                    fontWeight: 500
                  }}
                >
                  <span className="font-mono text-xs">{address}</span>
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>

              {isCorrectChain && balanceData && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide" style={{ fontWeight: 500, color: 'rgba(229, 229, 229, 0.7)' }}>Balance</div>
                  <div className="px-3 py-2 rounded border" style={{ backgroundColor: '#0D1117', borderColor: '#2D2F36', borderRadius: 'var(--radius-sm)' }}>
                    <span className="text-sm font-semibold" style={{ fontWeight: 600, color: '#E5E5E5' }}>{balance} {nativeSymbol}</span>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  disconnect();
                  setShowWalletDetails(false);
                }}
                className="w-full px-4 py-2 rounded-lg border transition-colors"
                style={{ 
                  backgroundColor: '#EF4444', 
                  borderColor: '#EF4444', 
                  color: '#FFFFFF',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 600
                }}
              >
                Disconnect
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => openConnectModal?.()}
      className="px-4 py-2 sm:py-2.5 rounded-lg border transition-all"
      style={{ 
        backgroundColor: '#2081E2', 
        borderColor: '#2081E2', 
        color: '#FFFFFF',
        borderRadius: 'var(--radius-sm)',
        fontWeight: 600
      }}
    >
      Connect Wallet
    </button>
  );
}
